import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../store';

const translations = {
  en: {
    title: 'Create New Password',
    label: 'New Password',
    button: 'Update Password',
    success: 'Your password has been reset successfully. You can now proceed to log in.',
    errorToken: 'Invalid or missing reset token. Please request a new link.',
    reqTitle: 'Password Requirements:',
    reqLen: 'At least 8 characters long',
    reqUpper: 'One uppercase letter',
    reqLower: 'One lowercase letter',
    reqDigit: 'One digit',
    reqSpecial: 'One special character (!@#$%^&*)',
    reqError: 'Password does not meet requirements.',
    goToLogin: 'Go to Login'
  },
  ru: {
    title: 'Новый пароль',
    label: 'Новый пароль',
    button: 'Обновить пароль',
    success: 'Пароль успешно изменен. Теперь вы можете войти в свой аккаунт.',
    errorToken: 'Неверный или отсутствующий токен. Пожалуйста, запросите ссылку заново.',
    reqTitle: 'Требования к паролю:',
    reqLen: 'Не менее 8 символов',
    reqUpper: 'Минимум одна заглавная буква',
    reqLower: 'Минимум одна строчная буква',
    reqDigit: 'Минимум одна цифра',
    reqSpecial: 'Минимум один спецсимвол (!@#$%^&*)',
    reqError: 'Пароль не соответствует требованиям.',
    goToLogin: 'Войти в аккаунт'
  },
  nl: {
    title: 'Nieuw Wachtwoord Aanmaken',
    label: 'Nieuw Wachtwoord',
    button: 'Wachtwoord Bijwerken',
    success: 'Uw wachtwoord is succesvol gewijzigd. U kunt nu inloggen.',
    errorToken: 'Ongeldige of ontbrekende token. Vraag een nieuwe link aan.',
    reqTitle: 'Wachtwoordvereisten:',
    reqLen: 'Ten minste 8 tekens lang',
    reqUpper: 'Eén hoofdletter',
    reqLower: 'Eén kleine letter',
    reqDigit: 'Eén cijfer',
    reqSpecial: 'Eén speciaal teken (!@#$%^&*)',
    reqError: 'Wachtwoord voldoet niet aan de vereisten.',
    goToLogin: 'Ga naar inloggen'
  },
  ro: {
    title: 'Creați o Parolă Nouă',
    label: 'Parolă Nouă',
    button: 'Actualizează Parola',
    success: 'Parola a fost resetată cu succes. Vă puteți autentifica acum.',
    errorToken: 'Token invalid sau lipsă. Vă rugăm să solicitați un link nou.',
    reqTitle: 'Cerințe parolă:',
    reqLen: 'Cel puțin 8 caractere',
    reqUpper: 'O literă mare',
    reqLower: 'O literă mică',
    reqDigit: 'O cifră',
    reqSpecial: 'Un caracter special (!@#$%^&*)',
    reqError: 'Parola nu îndeplinește cerințele.',
    goToLogin: 'Mergi la autentificare'
  }
};

export default function ResetPassword() {
  // Language from the store (URL-driven via LanguageRoute), not a stale prop.
  const { language } = useLanguageStore();
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const t = translations[language] || translations.en;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setStatus(prev => ({ ...prev, error: t.errorToken }));
    }
  }, [t.errorToken]);

  const reqLen = password.length >= 8;
  const reqUpper = /[A-Z]/.test(password);
  const reqLower = /[a-z]/.test(password);
  const reqDigit = /[0-9]/.test(password);
  const reqSpecial = /[!@#$%^&*(),.?":{}|<>_\-]/.test(password);
  const isPasswordValid = reqLen && reqUpper && reqLower && reqDigit && reqSpecial;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;

    if (!isPasswordValid) {
      setStatus({ loading: false, success: false, error: t.reqError });
      return;
    }

    setStatus({ loading: true, success: false, error: '' });

    try {
      const response = await fetch('https://fedyk-furniture-backend.onrender.com/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Error resetting password');
      }

      setStatus({ loading: false, success: true, error: '' });
      setPassword('');
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.message || 'Error occurred.' });
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center px-4 py-12 font-sans selection:bg-black selection:text-white">
      <div className="max-w-md w-full bg-white p-10 md:p-12 border border-[#ECECEC]">
        
        {/* 🎯 Исправлено: Добавлен пробел в логотипе FEDYK Furniture */}
        <div className="mb-10 text-left border-b border-[#ECECEC] pb-6">
          <Link to={`/${language}`} className="inline-block">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-[#0A0A0A]">
              FEDYK <span className="font-light tracking-normal">Furniture</span>
            </span>
          </Link>
        </div>

        <h1 className="text-2xl font-light text-[#0A0A0A] tracking-tight mb-8 font-serif">{t.title}</h1>

        {status.success ? (
          <div className="space-y-6">
            <div className="p-4 bg-[#FAFAFA] border-l-2 border-[#0A0A0A] text-sm text-[#0A0A0A] font-light leading-relaxed">
              {t.success}
            </div>
            {/* 🎯 Кнопка входа после успешной смены пароля */}
            <Link 
              to={`/${language}/login`}
              className="block w-full text-center bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.25em] uppercase py-4 transition-colors font-light"
            >
              {t.goToLogin}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-[10px] tracking-[0.18em] uppercase text-[#9E9E9E] mb-2">
                {t.label}
              </label>
              <input
                id="password"
                type="password"
                required
                disabled={!token}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#ECECEC] px-4 py-3 text-sm text-[#0A0A0A] font-light focus:outline-none focus:border-[#0A0A0A] transition-colors disabled:opacity-50"
              />
            </div>

            {password.length > 0 && (
              <div className="text-[11px] text-[#9E9E9E] space-y-1 bg-[#FAFAFA] p-4 font-light border border-[#ECECEC]">
                <p className="font-medium text-[#4A4A4A] mb-2">{t.reqTitle}</p>
                <p className={reqLen ? "text-green-600 transition-colors" : ""}>{reqLen ? '✓' : '•'} {t.reqLen}</p>
                <p className={reqUpper ? "text-green-600 transition-colors" : ""}>{reqUpper ? '✓' : '•'} {t.reqUpper}</p>
                <p className={reqLower ? "text-green-600 transition-colors" : ""}>{reqLower ? '✓' : '•'} {t.reqLower}</p>
                <p className={reqDigit ? "text-green-600 transition-colors" : ""}>{reqDigit ? '✓' : '•'} {t.reqDigit}</p>
                <p className={reqSpecial ? "text-green-600 transition-colors" : ""}>{reqSpecial ? '✓' : '•'} {t.reqSpecial}</p>
              </div>
            )}

            {status.error && (
              <p className="text-xs text-[#D32F2F] font-light tracking-wide">{status.error}</p>
            )}

            <button
              type="submit"
              disabled={status.loading || !token || (password.length > 0 && !isPasswordValid)}
              className="w-full bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.25em] uppercase py-4 transition-colors font-light disabled:opacity-50"
            >
              {status.loading ? '...' : t.button}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
